import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getTimetableRun, listClasses, listSubjects, listStaff, TEACHER_DEPARTMENT, DAYS, SESSIONS } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

const PERIOD_LABELS = SESSIONS.flatMap((ses) => ses.periods.map((p, i) => `${ses.label} ${i + 1}`));

const HappySchoolTimetableView = () => {
  const { runId } = useParams();
  const schoolId = getActiveSchoolId();
  const [run, setRun] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [error, setError] = useState("");
  const [view, setView] = useState("class");
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    if (!schoolId || !runId) return;
    Promise.all([getTimetableRun(schoolId, runId), listClasses(schoolId), listSubjects(schoolId), listStaff(schoolId, { department: TEACHER_DEPARTMENT })])
      .then(([r, c, s, t]) => {
        setRun(r);
        setClasses(c);
        setSubjects(s);
        setTeachers(t);
        if (c.length > 0) setSelectedId(c[0].id);
      })
      .catch((err) => setError(err.message));
  }, [schoolId, runId]);

  const subjectName = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s.name])), [subjects]);
  const teacherName = useMemo(() => Object.fromEntries(teachers.map((t) => [t.id, t.name])), [teachers]);
  const className = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c.name])), [classes]);
  // Chỉ những GV thực sự có tiết trong bản TKB đang xem mới vào combobox.
  const teachersWithLessons = useMemo(() => {
    const ids = new Set(Object.values(run?.grid || {}).filter(Boolean).map(([, tId]) => tId));
    return teachers.filter((t) => ids.has(t.id));
  }, [run, teachers]);

  useEffect(() => {
    const list = view === "class" ? classes : teachersWithLessons;
    if (list.length > 0 && !list.find((x) => x.id === selectedId)) setSelectedId(list[0].id);
    // eslint-disable-next-line
  }, [view, classes, teachersWithLessons]);

  if (!schoolId) {
    return (
      <div style={{ padding: 24 }}>
        Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link> trước.
      </div>
    );
  }
  if (error) return <div style={{ padding: 24, color: "#dc2626" }}>{error}</div>;
  if (!run) return <div style={{ padding: 24 }}>Đang tải...</div>;
  if (run.status !== "done") return <div style={{ padding: 24 }}>Kết quả chưa sẵn sàng (trạng thái: {run.status}).</div>;

  const grid = run.grid || {};
  const totalPeriods = SESSIONS.reduce((acc, s) => acc + s.periods.length, 0);

  const cellFor = (day, period) => {
    if (view === "class") {
      const entry = grid[`${selectedId}|${day}|${period}`];
      if (!entry) return "";
      const [subjectId, teacherId] = entry;
      const tName = teacherId ? teacherName[teacherId] || teacherId : "";
      return tName ? `${subjectName[subjectId] || subjectId}\n${tName}` : `${subjectName[subjectId] || subjectId}`;
    }
    // theo giáo viên: quét tất cả lớp tìm slot mà GV này dạy vào (day,period)
    for (const key of Object.keys(grid)) {
      const [classId, d, p] = key.split("|");
      if (Number(d) === day && Number(p) === period) {
        const [subjectId, teacherId] = grid[key];
        if (teacherId === selectedId) {
          return `${className[classId] || classId}\n${subjectName[subjectId] || subjectId}`;
        }
      }
    }
    return "";
  };

  const options = view === "class" ? classes : teachersWithLessons;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 16px 0" }}>Kết quả thời khoá biểu</h2>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
        <label>
          <input type="radio" checked={view === "class"} onChange={() => setView("class")} /> Theo lớp
        </label>
        <label>
          <input type="radio" checked={view === "teacher"} onChange={() => setView("teacher")} /> Theo giáo viên
        </label>
        <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} style={{ padding: 6 }}>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
          <thead>
            <tr>
              <th style={thStyle}>Tiết</th>
              {DAYS.map((d) => (
                <th key={d} style={thStyle}>
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: totalPeriods }, (_, p) => (
              <tr key={p}>
                <td style={thStyle}>{PERIOD_LABELS[p]}</td>
                {DAYS.map((_, day) => (
                  <td key={day} style={tdStyle}>
                    {cellFor(day, p)
                      .split("\n")
                      .map((line, i) => (
                        <div key={i} style={i === 0 ? { fontWeight: 600 } : { color: "#6b7280" }}>
                          {line}
                        </div>
                      ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const thStyle = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb" };
const tdStyle = { border: "1px solid #e5e7eb", padding: "6px 8px", minWidth: 90, textAlign: "center" };

export default HappySchoolTimetableView;
